import { Divider, Page, Spinner } from "@whitespace/whitespace-ui";
import { useNavigate, Link } from "react-router-dom";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";

import {
  Button,
  Card,
  Col,
  ListGroup,
  Row,
  Form,
  Breadcrumb
} from "react-bootstrap";
import { useCallback, useEffect, useState } from "react";
import "./EditSla.scss";
import {
  convertHoursAndMinutesToDecimal,
  formatDecimalToHoursAndMinutes
} from "helpers/DateUtils";
import useConfig from "hooks/useConfig";
import { useForm } from "react-hook-form";
import IGetSlaDetailsResponseModel from "interfaces/Response/IGetSlaDetailsResponseModel";
import ISlaDetailsViewModel from "interfaces/ISlaDetailsViewModel";
import { format } from "date-fns";
import IUpdateSlaDetailsRequestModel from "interfaces/Request/IUpdateSlaDetailsRequestModel";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import { mapSlaDetailsToViewModel } from "./DataMappers/SlaDetailsMapper";

const EditSla = () => {
  const {
    isSending: getSlaDetailsIsSending,
    errors: getSlaDetailsErrors,
    data: getSlaDetailsData,
    sendRequest: getSlaDetailsSendRequest
  } = useAmplifyREST();
  const {
    isSending: updateSlaDetailsIsSending,
    errors: updateSlaDetailsErrors,
    sendRequest: updateSlaDetailsSendRequest,
    statusCode: updateSlaDetailsStatusCode
  } = useAmplifyREST();

  const isSending = getSlaDetailsIsSending || updateSlaDetailsIsSending;
  const apiErrors = getSlaDetailsErrors || updateSlaDetailsErrors;

  const config = useConfig();
  const navigate = useNavigate();

  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [slaDefaultTime, setSlaDefaultTime] = useState<number>();
  const [slaDetailsViewModel, setSlaDetailsViewModel] = useState<
  ISlaDetailsViewModel | undefined
  >();
  const [slaSelected, setSlaSelected] = useState<number | undefined>();
  const [slaTimes, setSlaTimes] = useState<number[]>();
  const [updatedSlaTimes, setUpdatedSlaTimes] = useState<number[]>();
  const [addButtonDisabled, setAddButtonDisabled] = useState<boolean>(true);
  const [canFormBeSubmitted, setCanFormBeSubmitted] = useState<boolean>(false);
  const [anySlaMoreThanLimit, setAnySlaMoreThanLimit] = useState<boolean>(false);

  const {
    register, getValues, watch, handleSubmit
  } = useForm({});

  const watchFields = watch(["slaHour", "slaMinute"]);

  useEffect(() => {
    getSlaDetailsSendRequest("settings/sla/details", "GET");
  }, [getSlaDetailsSendRequest]);

  useEffect(() => {
    if (getSlaDetailsData) {
      const response = getSlaDetailsData as IGetSlaDetailsResponseModel;
      setSlaDetailsViewModel(mapSlaDetailsToViewModel(response.slaDetails));

      setSlaDefaultTime(response.slaDetails.slaDefaultTime);

      setSlaTimes([...response.slaDetails.slaTimes.sort()]);
      setAddButtonDisabled(
        response.slaDetails.slaTimes!.length === config.slaMaxTimes
      );
    }
  }, [getSlaDetailsData]);

  useEffect(() => {
    if (apiErrors) {
      setShowExceptions(true);
    }
  }, [apiErrors]);

  useEffect(() => {
    if (slaTimes) {
      const slaTimesChanged = !(
        slaTimes.every(item => slaDetailsViewModel!.slaTimes.includes(item))
        && slaDetailsViewModel!.slaTimes.every(item => slaTimes.includes(item))
      );
      const slaDefaultChanged = slaDefaultTime !== undefined
        && slaDefaultTime !== slaDetailsViewModel!.slaDefaultTime;

      const canFormBeSubmittedCheck = (slaTimesChanged
          && slaTimes.length !== 0
          && slaDefaultTime !== undefined)
        || slaDefaultChanged;

      setCanFormBeSubmitted(canFormBeSubmittedCheck);

      const currentSlaHour: number = Number(getValues("slaHour"));
      const currentSlaMinute: number = Number(getValues("slaMinute"));

      const selectedHoursMins = convertHoursAndMinutesToDecimal(
        currentSlaHour,
        currentSlaMinute
      );
      setAddButtonDisabled(
        slaTimes!.length === config.slaMaxTimes
          || slaTimes!.includes(selectedHoursMins!)
          || (currentSlaHour === 0 && currentSlaMinute === 0)
      );
    }
  }, [watchFields, slaTimes, slaDefaultTime]);

  const handleDelete = () => {
    const newSlaTimes = slaTimes?.filter(x => x !== slaSelected);
    setSlaTimes(newSlaTimes);
    setSlaSelected(undefined);
    if (!newSlaTimes?.includes(slaDefaultTime!)) {
      setSlaDefaultTime(undefined);
    }
  };

  const renderSlaTimes = (times: number[]) => (
    <div className="text-center w-10">
      <ListGroup
        as="ul"
        defaultValue={slaDefaultTime}
        data-testid="slaList"
        className="d-flex flex-col slaList"
      >
        {times?.map((slaTime: number) => (
          <ListGroup.Item
            key={`sla_${slaTime}`}
            as="li"
            className={`d-flex ${slaTime === slaSelected ? "selected" : ""}`}
          >
            <input
              id={`slaDefault_${slaTime}`}
              data-testid={`slaDefault_${slaTime}`}
              type="radio"
              name="slaDefault"
              aria-label={`SLA time ${slaTime}`}
              value={slaTime}
              defaultChecked={slaTime === slaDefaultTime}
              onClick={() => setSlaDefaultTime(slaTime)}
              className="me-2 d-inline-flex"
            />
            <button
              type="button"
              id={`btnSlaTimeSelection_${slaTime}`}
              data-testid={`btnSlaTimeSelection_${slaTime}`}
              className="d-inline-flex w-100"
              onClick={() => setSlaSelected(slaTime)}
            >
              <span id={`slaDefault_${slaTime}_label`}>
                {formatDecimalToHoursAndMinutes(slaTime)}
              </span>
            </button>
          </ListGroup.Item>
        ))}
      </ListGroup>
      <Button
        id="btnDeleteSlaTime"
        data-testid="btnDeleteSlaTime"
        variant="danger"
        className="mt-2"
        disabled={!slaSelected}
        onClick={handleDelete}
      >
        Delete
      </Button>
    </div>
  );

  const renderSlaHours = () => (
    <div className="d-inline-flex align-items-center">
      <Form.Select
        {...register("slaHour")}
        data-testid="ddlHours"
        aria-required="true"
        aria-label="Select an hour"
        className="me-1"
      >
        {config.slaTimeHours.map(hour => (
          <option key={hour} value={`${hour}`}>
            {`${hour}`}
          </option>
        ))}
      </Form.Select>
      <span id="newSlaTimeHourLabel ms-3">Hour(s)</span>
    </div>
  );

  const renderSlaMinutes = () => (
    <div className="d-inline-flex align-items-center">
      <Form.Select
        {...register("slaMinute")}
        data-testid="ddlMinutes"
        aria-required="true"
        aria-label="Select a minute"
        className="me-1 ms-2"
      >
        {config.slaTimeMinutes.map(minute => (
          <option key={minute} value={`${minute}`}>
            {`${minute}`}
          </option>
        ))}
      </Form.Select>
      <span id="newSlaTimeMinuteLabel">Minute(s)</span>
    </div>
  );

  const handleAdd = () => {
    const newSla = convertHoursAndMinutesToDecimal(
      Number(getValues("slaHour")),
      Number(getValues("slaMinute"))
    );

    if (newSla! <= config.slaMaxHourAndMinutesInDecimal) {
      slaTimes?.push(newSla!);
      setUpdatedSlaTimes(slaTimes?.sort((n1, n2) => n1 - n2));
      setAnySlaMoreThanLimit(false);
    } else {
      setAnySlaMoreThanLimit(true);
    }
  };

  const handleUpdateSlaDetailsClick = () => {
    const request: IUpdateSlaDetailsRequestModel = {
      slaDefaultTime: slaDefaultTime!,
      slaTimes: slaTimes!
    };
    updateSlaDetailsSendRequest("settings/sla", "PUT", request);
  };

  useEffect(() => {
    if (updateSlaDetailsStatusCode && updateSlaDetailsStatusCode === 200) {
      navigate(RoutePathsEnum.SettingsSla);
    }
  }, [updateSlaDetailsStatusCode]);

  useEffect(() => {
    if (updatedSlaTimes) {
      setSlaTimes(updatedSlaTimes);
      setUpdatedSlaTimes(undefined);
    }
  }, [updatedSlaTimes]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="edit-sla">
              Edit SLA
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Card.Body className="no-padding">
          {slaDetailsViewModel && (
            <>
              <Form
                autoComplete="off"
                onSubmit={handleSubmit(handleUpdateSlaDetailsClick)}
              >
                <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
                  <Col xs={6} className="d-none d-md-block">
                    <Breadcrumb className="left-margin-large no-wrap">
                      <Breadcrumb.Item
                        data-testid="breadcrumb-settings"
                        linkAs={Link}
                        linkProps={{ to: RoutePathsEnum.Settings }}
                        onClick={() => navigate(RoutePathsEnum.Settings)}
                      >
                        Settings
                      </Breadcrumb.Item>
                      <Breadcrumb.Item
                        data-testid="breadcrumb-sla-management"
                        linkAs={Link}
                        linkProps={{ to: RoutePathsEnum.SettingsSla }}
                        onClick={() => navigate(RoutePathsEnum.SettingsSla)}
                      >
                        SLA Management
                      </Breadcrumb.Item>
                      <Breadcrumb.Item data-testid="breadcrumb-edit-sla" active>
                        Edit SLA
                      </Breadcrumb.Item>
                    </Breadcrumb>
                  </Col>
                  <Col className="text-end">
                    <Button
                      id="btnUpdate"
                      disabled={!canFormBeSubmitted}
                      variant="primary"
                      className="right-margin-small"
                      data-testid="btnUpdate"
                      aria-label="Update SLA"
                      type="submit"
                    >
                      Update
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => navigate(RoutePathsEnum.SettingsSla)}
                      data-testid="btnCancel"
                      aria-label="Cancel and back to Settings"
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
                <Divider />
                <Row className="ms-2 mt-2">
                  <h3 className="sla-options-heading" data-testid="sub-heading">
                    Set SLA Options for Collections
                  </h3>
                  <p data-testid="maxEntriesText">
                    {`There are a maximum of ${config.slaMaxTimes} entries.`}
                  </p>
                  <p data-testid="radioButtonDefaultText">
                    Select the radio button against the SLA option to set the
                    default.
                  </p>
                </Row>
                <Divider />

                {anySlaMoreThanLimit && (
                  <span
                    data-testid="anySlaMoreThanLimitException"
                    className="validation-error"
                  >
                    SLA duration must not exceed 7 hours.
                  </span>
                )}
                <Row className="mt-4 ms-2">
                  <Col>
                    <div className="d-inline-flex align-center">
                      {renderSlaHours()}
                      {renderSlaMinutes()}
                      <Button
                        id="btnAddSlaTime"
                        data-testid="btnAddSlaTime"
                        className="ms-1"
                        disabled={addButtonDisabled}
                        onClick={handleAdd}
                      >
                        Add
                      </Button>
                    </div>
                  </Col>
                </Row>
                <Row className="mt-2 ms-2">
                  <Col xs={12} md={3} lg={2} className="d-block">
                    {slaTimes && renderSlaTimes(slaTimes)}
                  </Col>
                  <Col />
                </Row>
                {slaDetailsViewModel.lastModifiedBy
                  && slaDetailsViewModel.lastModifiedTimestamp && (
                  <Row className="mt-2 ms-2">
                    <Col>
                      <p data-testid="lastModifiedByText">
                        {`Last Modified by: ${
                          slaDetailsViewModel.lastModifiedBy
                        } on 
                      ${format(
                    slaDetailsViewModel.lastModifiedTimestamp!,
                    "dd/MM/yyyy HH:mm:ss"
                  )}`}
                      </p>
                    </Col>
                  </Row>
                )}
              </Form>
            </>
          )}
        </Card.Body>
      </Card>
    </Page>
  );
};

export default EditSla;
